import React, { useState } from "react";
import { Box, IconButton, Typography } from "@mui/material";
import { ArrowBackIos, ArrowForwardIos } from "@mui/icons-material";
import { useTheme } from "@mui/material/styles";
import CustomTypography from "../form/CustomTypography";

interface ImageCarouselProps {
  images?: Array<{ image_url: string } | string> | string | null;
  emptyMessage?: string;
  mainImageHeight?: string | number;
  mainImageWidth?: string | number;
  thumbnailSize?: string | number;
}

const ImageCarousel: React.FC<ImageCarouselProps> = ({
  images = [],
  emptyMessage = "NO IMAGES FOUND",
  mainImageHeight = "16rem",
  mainImageWidth = "16rem",
  thumbnailSize = "3rem",
}) => {
  const theme = useTheme();
  const [currentIndex, setCurrentIndex] = useState(0);
  const [imageError, setImageError] = useState(false);

  const normalizedImages = React.useMemo(() => {
    if (!images) return [];

    if (typeof images === "string") return [{ image_url: images }];

    return images.map((img) =>
      typeof img === "string" ? { image_url: img } : img
    );
  }, [images]);

  const handlePrev = () => {
    setCurrentIndex((prev) => Math.max(0, prev - 1));
    setImageError(false);
  };

  const handleNext = () => {
    setCurrentIndex((prev) => Math.min(normalizedImages.length - 1, prev + 1));
    setImageError(false);
  };

  const handleThumbnailClick = (index: number) => {
    setCurrentIndex(index);
    setImageError(false);
  };

  const handleImageError = () => {
    setImageError(true);
  };

  const currentImage = normalizedImages[currentIndex]?.image_url;

  if (normalizedImages.length === 0) {
    return (
      <Box
        display="flex"
        alignItems="center"
        justifyContent="center"
        height="24rem"
        mt={2.5}
        bgcolor={theme.palette.customColors?.greyBackground || "#f5f5f5"}
      >
        <CustomTypography
          variant="heading7"
          fontWeight={700}
          color={theme.palette.customColors.greyTextColor}
        >
          {emptyMessage}
        </CustomTypography>
      </Box>
    );
  }

  return (
    <Box mt={2.5}>
      <Box sx={{ position: "relative" }}>
        <Box
          sx={{
            width: "100%",
            height: "24rem",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            backgroundColor:
              theme.palette.customColors?.greyBackground || "#f5f5f5",
            position: "relative",
          }}
        >
          {imageError || !currentImage ? (
            <Box
              display="flex"
              alignItems="center"
              justifyContent="center"
              height={mainImageHeight}
              width={mainImageWidth}
            >
              <Typography variant="body1">Image not found</Typography>
            </Box>
          ) : (
            <img
              src={currentImage}
              alt={`Image ${currentIndex + 1}`}
              onError={handleImageError}
              style={{
                width: mainImageWidth,
                height: mainImageHeight,
                objectFit: "contain",
                maxWidth: "100%",
                maxHeight: "100%",
              }}
            />
          )}

          {normalizedImages.length > 1 && (
            <>
              <IconButton
                onClick={handlePrev}
                disabled={currentIndex === 0}
                sx={{
                  position: "absolute",
                  left: 5,
                  top: "45%",
                  transform: "translateY(-50%)",
                  color: theme.palette.customColors?.blackBorder || "#000",
                  "&:hover": {
                    backgroundColor:
                      theme.palette.customColors?.white || "#fff",
                  },
                }}
              >
                <ArrowBackIos sx={{paddingLeft:"5px"}} />
              </IconButton>

              <IconButton
                onClick={handleNext}
                disabled={currentIndex === normalizedImages.length - 1}
                sx={{
                  position: "absolute",
                  right: 5,
                  top: "45%",
                  transform: "translateY(-50%)",
                  color: theme.palette.customColors?.blackBorder || "#000",
                  "&:hover": {
                    backgroundColor:
                      theme.palette.customColors?.white || "#fff",
                  },
                }}
              >
                <ArrowForwardIos sx={{paddingLeft:"4px"}} />
              </IconButton>
            </>
          )}

          {normalizedImages.length > 1 && (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                mt: 2,
                justifyContent: "center",
                gap: 0.5,
                flexWrap: "wrap",
              }}
            >
              {normalizedImages.map((img, index) => {
                const thumbnailError = !img.image_url;
                return (
                  <Box
                    key={index}
                    onClick={() => handleThumbnailClick(index)}
                    sx={{
                      width: thumbnailSize,
                      height: thumbnailSize,
                      borderRadius: "6px",
                      border:
                        currentIndex === index
                          ? `2px solid ${
                              theme.palette.customColors?.purple || "#9c27b0"
                            }`
                          : "1px solid #ddd",
                      overflow: "hidden",
                      cursor: "pointer",
                      opacity: currentIndex === index ? 1 : 0.7,
                      transition: "all 0.2s ease",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      backgroundColor: thumbnailError
                        ? "#f0f0f0"
                        : "transparent",
                      padding: "0.3rem",
                    }}
                  >
                    {thumbnailError ? (
                      <Typography variant="caption" color="textSecondary">
                        Error
                      </Typography>
                    ) : (
                      <img
                        src={img.image_url}
                        alt={`Thumbnail ${index + 1}`}
                        onError={(e) => {
                          const target = e.target as HTMLImageElement;
                          target.onerror = null;
                          target.src = ""; // Clear src to prevent infinite loop
                        }}
                        style={{
                          width: "100%",
                          height: "100%",
                          objectFit: "cover",
                        }}
                      />
                    )}
                  </Box>
                );
              })}
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default ImageCarousel;
